import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Printer, Download, X } from 'lucide-react';
import api from '../../lib/api';

const fmtRs = (val) => {
  const num = parseFloat(val) || 0;
  return 'Rs. ' + num.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' });
};

const statusLabel = (status) => {
  const map = {
    draft: 'Draft',
    confirmed: 'Confirmed',
    paid: 'Paid',
    partially_paid: 'Partially Paid',
    cancelled: 'Cancelled',
  };
  return map[status] || status;
};

const InvoicePrint = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    Promise.all([api.get(`/invoices/${id}`), api.get('/tenant/profile')])
      .then(([invRes, profRes]) => {
        setInvoice(invRes.data);
        setProfile(profRes.data);
      })
      .catch((err) => {
        console.error(err);
        setError('Failed to load invoice. Please close this tab and try again.');
      })
      .finally(() => setLoading(false));
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <p className="text-gray-500 text-lg">Loading invoice...</p>
      </div>
    );
  }

  if (error || !invoice || !profile) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-white">
        <p className="text-red-500 text-lg">{error || 'Invoice not found.'}</p>
      </div>
    );
  }

  const isPaid = invoice.status === 'paid';
  const balanceDue = parseFloat(invoice.balance_due) || 0;
  const subtotal = parseFloat(invoice.subtotal) || 0;
  const discountTotal = parseFloat(invoice.discount_total) || 0;
  const gstAmount = parseFloat(invoice.gst_amount) || 0;
  const totalAmount = parseFloat(invoice.total_amount) || 0;
  const amountPaid = parseFloat(invoice.amount_paid) || 0;

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; background: white; }
          @page { size: A4; margin: 12mm 14mm; }
          .invoice-wrapper { padding-top: 0 !important; }
          .watermark { position: fixed !important; }
        }
        .invoice-wrapper { font-family: 'Segoe UI', Arial, sans-serif; }
      `}</style>

      {/* ── Action bar (hidden on print) ── */}
      <div className="no-print fixed top-0 left-0 right-0 z-50 bg-gray-100 border-b border-gray-300 px-6 py-3 flex items-center gap-3 shadow-sm">
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-gradient-to-r from-[#22B8D4] via-[#4A7AE0] to-[#7C3BD6] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Printer className="w-4 h-4" />
          Print Invoice
        </button>
        <button
          onClick={() => window.print()}
          className="flex items-center gap-2 bg-white border border-[#e5e7eb] text-[#1a1a2e] hover:bg-[#f9fafb] px-4 py-2 rounded-lg text-sm font-medium transition-colors"
        >
          <Download className="w-4 h-4" />
          Download PDF
        </button>
        <div className="ml-auto">
          <button
            onClick={() => window.close()}
            className="flex items-center gap-1 text-gray-500 hover:text-gray-800 text-sm"
          >
            <X className="w-4 h-4" />
            Close
          </button>
        </div>
      </div>

      {/* ── Invoice page ── */}
      <div className="invoice-wrapper bg-white min-h-screen pt-16 pb-12">
        <div className="relative max-w-[820px] mx-auto px-10 py-8">

          {/* PAID watermark */}
          {isPaid && (
            <div
              className="watermark pointer-events-none fixed inset-0 flex items-center justify-center"
              style={{ zIndex: 0 }}
            >
              <span
                style={{
                  transform: 'rotate(-45deg)',
                  fontSize: '140px',
                  fontWeight: '900',
                  color: 'rgba(74, 122, 224, 0.10)',
                  letterSpacing: '8px',
                  userSelect: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                PAID
              </span>
            </div>
          )}

          {/* Content sits above watermark */}
          <div style={{ position: 'relative', zIndex: 1 }}>

            {/* ── Header ── */}
            <div className="flex justify-between items-start mb-8">
              {/* Company info */}
              <div>
                <h1 className="text-2xl font-bold text-[#1E40AF] leading-tight">
                  {profile.company_name}
                </h1>
                {profile.company_address && (
                  <p className="text-sm text-gray-600 mt-1">{profile.company_address}</p>
                )}
                {profile.company_city && (
                  <p className="text-sm text-gray-600">{profile.company_city}</p>
                )}
                {profile.company_phone && (
                  <p className="text-sm text-gray-600">{profile.company_phone}</p>
                )}
                {profile.company_ntn && (
                  <p className="text-sm text-gray-600">NTN: {profile.company_ntn}</p>
                )}
              </div>

              {/* INVOICE label */}
              <div className="text-right">
                <h2
                  className="font-extrabold text-[#1E40AF] tracking-widest"
                  style={{ fontSize: '48px', lineHeight: 1 }}
                >
                  INVOICE
                </h2>
                <p className="text-base font-semibold text-gray-700 mt-2">
                  {invoice.invoice_number}
                </p>
              </div>
            </div>

            {/* ── Divider ── */}
            <hr className="border-[#1E40AF] border-t-2 mb-6" />

            {/* ── Invoice details + Bill To ── */}
            <div className="grid grid-cols-2 gap-6 mb-8">
              {/* Invoice Details */}
              <div className="border border-gray-200 rounded overflow-hidden">
                <div className="bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold">
                  Invoice Details
                </div>
                <div className="px-4 py-3 text-sm space-y-2">
                  <Row label="Invoice No" value={invoice.invoice_number} />
                  <Row label="Issue Date" value={fmtDate(invoice.issue_date)} />
                  <Row label="Due Date" value={fmtDate(invoice.due_date)} />
                  <Row
                    label="Status"
                    value={
                      <span
                        className={`font-semibold ${
                          isPaid
                            ? 'text-blue-600'
                            : invoice.status === 'partially_paid'
                            ? 'text-yellow-600'
                            : invoice.status === 'cancelled'
                            ? 'text-red-600'
                            : 'text-blue-600'
                        }`}
                      >
                        {statusLabel(invoice.status)}
                      </span>
                    }
                  />
                </div>
              </div>

              {/* Bill To */}
              <div className="border border-gray-200 rounded overflow-hidden">
                <div className="bg-[#1E40AF] text-white px-4 py-2 text-sm font-semibold">
                  Bill To
                </div>
                <div className="px-4 py-3 text-sm">
                  <p className="font-semibold text-gray-800 text-base">
                    {invoice.customer?.name || '—'}
                  </p>
                  {invoice.customer?.address && (
                    <p className="text-gray-600 mt-1">{invoice.customer.address}</p>
                  )}
                  {invoice.customer?.city && (
                    <p className="text-gray-600">{invoice.customer.city}</p>
                  )}
                  {invoice.customer?.phone && (
                    <p className="text-gray-600 mt-1">{invoice.customer.phone}</p>
                  )}
                  {invoice.customer?.ntn && (
                    <p className="text-gray-600">NTN: {invoice.customer.ntn}</p>
                  )}
                </div>
              </div>
            </div>

            {/* ── Items table ── */}
            <table className="w-full text-sm mb-8" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#1E40AF', color: 'white' }}>
                  <th className="py-3 px-3 text-left" style={{ width: '42px' }}>Sr No</th>
                  <th className="py-3 px-3 text-left">Product Name</th>
                  <th className="py-3 px-3 text-right" style={{ width: '60px' }}>Qty</th>
                  <th className="py-3 px-3 text-right" style={{ width: '60px' }}>Unit</th>
                  <th className="py-3 px-3 text-right">Unit Price (Rs.)</th>
                  <th className="py-3 px-3 text-right">Discount (Rs.)</th>
                  <th className="py-3 px-3 text-right">Total (Rs.)</th>
                </tr>
              </thead>
              <tbody>
                {(invoice.items || []).map((item, idx) => {
                  const qty = parseFloat(item.quantity) || 0;
                  const unitPrice = parseFloat(item.unit_price) || 0;
                  const discPct = parseFloat(item.discount_percent) || 0;
                  const discAmt = qty * unitPrice * (discPct / 100);
                  const rowTotal = parseFloat(item.total) || 0;
                  return (
                    <tr
                      key={item.id || idx}
                      style={{ backgroundColor: idx % 2 === 0 ? '#ffffff' : '#F9FAFB' }}
                    >
                      <td className="py-2 px-3 text-gray-600" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {idx + 1}
                      </td>
                      <td className="py-2 px-3 font-medium text-gray-800" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {item.product?.name || '—'}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {qty % 1 === 0 ? qty : qty.toFixed(2)}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {item.product?.unit || '—'}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-700" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {unitPrice.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="py-2 px-3 text-right text-gray-600" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {discAmt > 0
                          ? discAmt.toLocaleString('en-PK', { minimumFractionDigits: 2 })
                          : '—'}
                      </td>
                      <td className="py-2 px-3 text-right font-medium text-gray-800" style={{ borderBottom: '1px solid #F0F0F0' }}>
                        {rowTotal.toLocaleString('en-PK', { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* ── Totals ── */}
            <div className="flex justify-end mb-6">
              <div style={{ width: '320px' }}>
                <TotalRow label="Subtotal" value={fmtRs(subtotal)} />
                {discountTotal > 0 && (
                  <TotalRow label="Discount" value={`- ${fmtRs(discountTotal)}`} />
                )}
                {gstAmount > 0 && (
                  <TotalRow
                    label={`GST${profile.gst_rate > 0 ? ` (${profile.gst_rate}%)` : ''}`}
                    value={fmtRs(gstAmount)}
                  />
                )}
                <div
                  className="flex justify-between items-center px-4 py-3 mt-1 rounded font-bold text-white text-sm"
                  style={{ backgroundColor: '#1E40AF' }}
                >
                  <span>Grand Total</span>
                  <span>{fmtRs(totalAmount)}</span>
                </div>
              </div>
            </div>

            {/* ── Payment history ── */}
            {invoice.payments && invoice.payments.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-700 mb-2 border-b border-gray-200 pb-1">
                  Payment History
                </h3>
                <div className="space-y-1">
                  {invoice.payments.map((pmt, idx) => (
                    <div key={pmt.id || idx} className="flex justify-between text-sm py-1">
                      <span className="text-gray-500">
                        {fmtDate(pmt.payment_date)}
                        {' — '}
                        {(pmt.payment_method || '').replace(/_/g, ' ')}
                        {pmt.reference ? ` (Ref: ${pmt.reference})` : ''}
                      </span>
                      <span className="text-blue-600 font-medium">{fmtRs(pmt.amount)}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between text-sm font-semibold mt-2 pt-2 border-t border-gray-200">
                  <span className="text-gray-700">Total Paid</span>
                  <span className="text-blue-600">{fmtRs(amountPaid)}</span>
                </div>
              </div>
            )}

            {/* ── Balance due ── */}
            <div
              className="flex justify-between items-center px-5 py-4 rounded font-bold text-base mb-6"
              style={{
                backgroundColor: isPaid ? '#EFF6FF' : '#FEF2F2',
                color: isPaid ? '#1E40AF' : '#B91C1C',
                border: `1px solid ${isPaid ? '#BFDBFE' : '#FECACA'}`,
              }}
            >
              <span>Balance Due</span>
              <span>{fmtRs(balanceDue)}</span>
            </div>

            {/* ── Bank details ── */}
            {profile.show_bank_details && profile.bank_name && (
              <div
                className="rounded p-4 mb-6 text-sm"
                style={{ border: '1px solid #DBEAFE', backgroundColor: '#EFF6FF' }}
              >
                <h3 className="font-semibold text-[#1E40AF] mb-2">Bank Details</h3>
                <div className="space-y-1 text-gray-700">
                  <p>
                    <span className="font-medium">Bank: </span>
                    {profile.bank_name}
                  </p>
                  {profile.bank_account && (
                    <p>
                      <span className="font-medium">Account No: </span>
                      {profile.bank_account}
                    </p>
                  )}
                  {profile.bank_iban && (
                    <p>
                      <span className="font-medium">IBAN: </span>
                      {profile.bank_iban}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* ── Notes ── */}
            {invoice.notes && (
              <div className="mb-6 text-sm text-gray-600">
                <span className="font-semibold text-gray-700">Notes: </span>
                {invoice.notes}
              </div>
            )}

            {/* ── Footer ── */}
            <div className="border-t-2 border-[#1E40AF] pt-4 text-center">
              <p className="text-[#1E40AF] font-semibold text-base">
                Thank you for your business!
              </p>
              {profile.company_phone && (
                <p className="text-sm text-gray-500 mt-1">{profile.company_phone}</p>
              )}
            </div>

          </div>{/* /relative content */}
        </div>{/* /max-w container */}
      </div>{/* /invoice-wrapper */}
    </>
  );
};

/* ── Small helpers ── */
const Row = ({ label, value }) => (
  <div className="flex justify-between items-start gap-2">
    <span className="text-gray-500 shrink-0">{label}:</span>
    <span className="text-gray-800 text-right">{value}</span>
  </div>
);

const TotalRow = ({ label, value }) => (
  <div
    className="flex justify-between text-sm py-1 px-4"
    style={{ borderBottom: '1px solid #F0F0F0' }}
  >
    <span className="text-gray-500">{label}</span>
    <span className="text-gray-800">{value}</span>
  </div>
);

export default InvoicePrint;
